'use strict';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { getCurrentTimeInSeconds } from '@helpers/get-current-time-in-seconds';
import { EditTrackingChangesKeys } from '@models/edit-tracking-changes';
import { EquationsTreeNode } from '@models/equations-tree-node';
import { AuxiliaryAccountPeriodEquation } from '@models/period-equation';
import { PeriodEquationsResource } from '@models/period-equations-resource-names.d';
import { AppConfigAPI } from '@models/types/appconfig.d';
import { UserEditTrackingItem } from '@models/user-edit-tracking-item';
import { AppConfigProvider } from '@root/app/app.config.provider';
import { TrackingService } from '@services/tracking/tracking.service';
import { backoff } from '@shared/helpers/backoff';
import { capitalizeString } from '@shared/helpers/capitalizeString';
import { convertBlankTo } from '@shared/helpers/convertBlankTo';
import { trimString } from '@shared/helpers/trimString';
import { Observable, of } from 'rxjs';
import { catchError, defaultIfEmpty, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class EquationsService {
  static preparedObject: any = {};
  public myUserId: number = null;
  public onLoadTimestamp: number = null;
  public onEditTimestamp: number = null;
  private environment: AppConfigAPI;

  constructor(private http: HttpClient, private tracking: TrackingService, private appConfig: AppConfigProvider) {
    this.appConfig.environment.subscribe(env => {
      this.environment = env.API;
    });
  }

  public getAuxiliaryPeriodEquations(auxiliary_mnemonic = null) {
    let url = `${this.environment.BALANCEAPI}/api/balancesheet/auxiliary/periodequations`;
    if (auxiliary_mnemonic) {
      url += `?auxiliary_mnemonic=${auxiliary_mnemonic}`;
    }
    return this.http
      .get<AuxiliaryAccountPeriodEquation[]>(url)
      .pipe(tap(x => {}))
      .toPromise();
  }

  /**
   * Get all equations from the target microservice URL
   */
  public getAll(baseApiUrl: string): Observable<object[]> {
    const result$ = this.http.get<object[]>(baseApiUrl).pipe(
      defaultIfEmpty([]),
      backoff(),
      catchError(err => {
        console.error(`Problem storing multiple resultset tracking data \nStacktrace: ${err}`);
        return [];
      }),
      tap(res => {
        console.log(res);
        this.tracking.updateModelDataChangesFlags('resultsets', true, `storeSingleTempResultset ${res}`);
      })
    );
    return result$;
  }

  /**
   * Obtain the complete list of period equations for a Resource based on la list of resources
   * @param resource String that represents a resource, like "Exogenous Lines"
   * @param idList A list of ids that will be used to iterate over the list of available resources
   *  i.e. [1, 3, 7] will fetch "Exogenous lines" 1, 3 and 7
   */
  public getAllPeriodEquations(resource: PeriodEquationsResource, idList: number[]): Promise<Object[]> {
    const result = idList.map(async (resourceId: number) => await this.getPeriodEquations(resource, -1, resourceId));
    return Promise.all(result);
  }

  /**
   * Get all the equations from a single source SID on the target microservice URL
   */
  public getOne(baseApiUrl: string, resourceId: number): Observable<object[]> {
    const url = `${baseApiUrl}/${resourceId}`;
    const result$ = this.http.get<object[]>(url) || of([]);
    return result$;
  }

  /**
   * Save changes in Period Equations and deletes tracking data associated
   */
  public async savePeriodEquations(
    resourceName: PeriodEquationsResource,
    spreadContent: object[],
    resourceId: number | string = null
  ): Promise<any> {
    let equationFieldIdentifier;
    let url;

    switch (resourceName) {
      case 'LiquiditySourcesPeriodEquations':
        equationFieldIdentifier = 'equation';
        url = `${this.environment.SOURCESAPI}/api/liquiditysources/periodequations/update`;
        if (resourceId) {
          url += `?resource_id=${resourceId}`;
        }
        break;
      case 'ExogenousLinesPeriodEquations':
        equationFieldIdentifier = 'Equation';
        url = `${this.environment.EXOGENOUSAPI}/api/exogenous/periodequations/update`;
        if (resourceId) {
          url += `?resource_id=${resourceId}`;
        }
        break;
      case 'AuxiliaryAccountsPeriodEquations':
        equationFieldIdentifier = 'equation';
        url = `${this.environment.BALANCEAPI}/api/balancesheet/auxiliary/periodequations/update`;
        if (resourceId) {
          url += `?auxiliary_mnemonic=${resourceId}`;
        }
    }

    // Converting null values to empty strings
    spreadContent = spreadContent.map(row => {
      row[equationFieldIdentifier] = convertBlankTo(row[equationFieldIdentifier], '');
      return row;
    });

    const savedItems = await this.http
      .post<object[]>(url, spreadContent)
      .pipe(
        // Update the global save button by checking the edit tracking table
        tap(() => this.tracking.getUserEditTrackingTable(resourceName))
      )
      .toPromise()
      .catch(() => null);

    if (savedItems) {
      // The associated tracking item should had been deleted by the store procedure
      return savedItems;
    }
    return null;
  }

  /**
   * Deletes the edit tracking item associated
   */
  public async deletePeriodEquationsTrackingItems(
    resourceName: PeriodEquationsResource,
    resourceId: number | string,
    myUserId: number = -1
  ): Promise<any> {
    let resourcePath;
    let baseApiUrl;

    switch (resourceName) {
      case 'LiquiditySourcesPeriodEquations':
        resourcePath = '/liquiditysources/periodequations';
        baseApiUrl = this.environment.TRACKINGAPI;
        break;
      case 'ExogenousLinesPeriodEquations':
        resourcePath = '/exogenous/periodequations';
        baseApiUrl = this.environment.TRACKINGAPI;
        break;
      case 'AuxiliaryAccountsPeriodEquations':
        return this.deleteAuxiliaryAccountPeriodEquationsTrackingItems(resourceId as string);
    }
    const url = `${baseApiUrl}/api/tracking${resourcePath}/delete?resource_id=${resourceId}`;
    const request$ = this.http.get(url).pipe(
      backoff(),
      catchError(err => {
        console.error(err);
        return err;
      }),
      // Update the global save button by checking the edit tracking table
      tap(_ => this.tracking.getUserEditTrackingTable(resourceName))
    );
    return await request$.toPromise();
  }

  /**
   * Deletes the edit tracking item associated
   */
  public async deleteAuxiliaryAccountPeriodEquationsTrackingItems(auxiliary_mnemonic: string): Promise<any> {
    const url = `${this.environment.TRACKINGAPI}/api/tracking/balancesheet/periodequations/delete?auxiliary_mnemonic=${auxiliary_mnemonic}`;
    const request$ = this.http.get(url).pipe(
      backoff(),
      catchError(err => {
        console.error(err);
        return err;
      }),
      // Update the global save button by checking the edit tracking table
      tap(_ => this.tracking.getUserEditTrackingTable('AuxiliaryAccountsPeriodEquations'))
    );
    return await request$.toPromise();
  }

  /**
   * Checks if exists a previous temporary record. If exists, returns an observable of it.
   * If it does not exist, calls the database to get data and returns the request observable
   */
  public async getPeriodEquations(
    resourceName: PeriodEquationsResource,
    userId: number,
    resourceId: number,
    forceNoTempRecord = false
  ): Promise<Object> {
    let model;
    let resourcePath;
    let baseApiUrl;

    switch (resourceName) {
      case 'LiquiditySourcesPeriodEquations':
        resourcePath = '/liquiditysources/periodequations';
        baseApiUrl = this.environment.SOURCESAPI;
        break;
      case 'ExogenousLinesPeriodEquations':
        resourcePath = '/exogenous/periodequations';
        baseApiUrl = this.environment.EXOGENOUSAPI;
        break;
    }

    const tempRecord = await this.getTemporaryRecordForPeriodEquations(resourcePath, userId, resourceId);
    this.onLoadTimestamp = getCurrentTimeInSeconds();
    this.onEditTimestamp = getCurrentTimeInSeconds();

    if (
      !forceNoTempRecord &&
      tempRecord &&
      tempRecord['table_data'] &&
      tempRecord['date_time_created'] &&
      tempRecord['table_data'] !== '[]'
    ) {
      const temporaryDataFound = JSON.parse(tempRecord['table_data']);
      this.onLoadTimestamp = temporaryDataFound['date_time_created'];
      model = {
        equations: temporaryDataFound || [],
        isFromTracking: true,
      };
    } else {
      const permanentDataFound = await this.getPeriodEquationsFromPermanentStorage(baseApiUrl, resourcePath, resourceId);
      model = {
        equations: permanentDataFound || [],
        isFromTracking: false,
      };
    }

    return model;
  }

  private async getPeriodEquationsFromPermanentStorage(targetUrl: string, resourcePath: string, resource_id: number) {
    // Make request to the database
    const url = `${targetUrl}/api${resourcePath}/?resource_id=${resource_id}`;

    const equations = await this.http
      .get<object[]>(url)
      .pipe(catchError(() => of(null)))
      .toPromise();
    if (equations && equations.hasOwnProperty('length') && equations.length > 0) {
      return equations;
    } else {
      console.error(`Any equations retrieved for id ${resource_id}`);
      return [];
    }
  }

  /**
   * Checks if exists a previous temporary record.
   * If it does, returns the data. If it does not exist, returns false
   */
  public async getTemporaryRecordForPeriodEquations(resourceEquationsPath: string, userId: number, resourceId: number): Promise<Object[]> {
    const url = `${this.environment.TRACKINGAPI}/api/tracking${resourceEquationsPath}?check_temp=1&resource_id=${resourceId}`;
    const temporaryRecord = await this.http
      .get<object[]>(url)
      .toPromise()
      .catch(() => null);
    return temporaryRecord;
  }

  /**
   * Parses the spreadsheed row content, applying autoconversions
   */
  public parsePeriodEquationsRow(row: object): object {
    Object.keys(row).forEach(key => {
      if (row.hasOwnProperty(key)) {
        let value = row[key];

        // Purge values
        // -- 1. Convert blank values to empty strings
        if (!value) {
          value = convertBlankTo(value, '');
        }

        // -- 2. Trim all strings and convert null values to empty strings
        value = trimString(value);

        // -- 3. Rules by column
        switch (key.toLowerCase()) {
          // Equation
          case 'equation':
            value = `${value}`; // Makes it string (bug fix 2286)

            // Add an equal sign to every equation
            // if (value.indexOf('=') === -1) {
            //   value = `=${value}`;
            // }
            break;
        }
        row[key] = value;
      }
    });
    return row;
  }

  /**
   * Parses the spreadsheed content, applying autoconversions
   */
  public parseSpreadContentForPeriodEquations(spreadContent: object[]): object[] {
    if (spreadContent && spreadContent.hasOwnProperty('length') && spreadContent.length > 0) {
      return spreadContent.map((row, index) => this.parsePeriodEquationsRow(row)) || [];
    }
    return [];
  }

  public serializePeriodEquationsData(spreadContent) {
    const processedData = JSON.stringify(spreadContent).replace(/\'/g, "''");
    return processedData;
  }

  public async storeTempPeriodEquations(
    resourceName: PeriodEquationsResource,
    spreadContent: object[],
    resourceId: number | string
  ): Promise<any> {
    let resourcePath;
    let trackingItemName;
    let field: EditTrackingChangesKeys;
    let debug: string;
    switch (resourceName) {
      case 'LiquiditySourcesPeriodEquations':
        resourcePath = '/liquiditysources/periodequations';
        trackingItemName = 'LiquiditySourcesPeriodEquations_Item';
        field = 'liquiditySources';
        debug = 'getAllLiquiditySourcesPeriodEquations';
        break;
      case 'ExogenousLinesPeriodEquations':
        resourcePath = '/exogenous/periodequations';
        trackingItemName = 'ExogenousLinesPeriodEquations_Item';
        field = 'exogenousLines';
        debug = 'getAllExogenousLinesPeriodEquations';
        break;
      case 'AuxiliaryAccountsPeriodEquations':
        return this.storeTempAuxiliaryPeriodEquations(spreadContent, resourceId as string);
    }
    const trackingItem: UserEditTrackingItem = {
      user_id: -1,
      date_time_created: '',
      table_data: this.serializePeriodEquationsData(spreadContent),
      edited_table: trackingItemName,
    };

    // Http request
    const url = `${this.environment.TRACKINGAPI}/api/tracking${resourcePath}?resource_id=${resourceId}`;
    return await this.http
      .post(url, trackingItem)
      .pipe(
        tap(() => {
          this.tracking.updateModelDataChangesFlags(field, true, debug);
        })
      )
      .toPromise();
  }

  public async storeTempAuxiliaryPeriodEquations(spreadContent: object[], resourceId: string): Promise<any> {
    spreadContent.forEach(row => {
      delete row['is_temp'];
    });

    const trackingItem = {
      date_time_created: '',
      table_data: this.serializePeriodEquationsData(spreadContent),
    };

    // Http request
    const url = `${this.environment.TRACKINGAPI}/api/tracking/balancesheet/periodequations?auxiliary_mnemonic=${resourceId}`;
    return await this.http
      .post(url, trackingItem)
      .pipe(
        tap(() => {
          this.tracking.updateModelDataChangesFlags('auxiliaryEquations', true, `storeTempAuxiliaryPeriodEquations`);
        })
      )
      .toPromise();
  }

  /**
   *  TREE-related methods
   */
  public treePreBuild(
    apiData,
    colNameForKey = 'name' /* 'exogenous_sid or source_sid'*/,
    colNameForValue = 'mnemonic' /* 'name'*/,
    colNameForGrouping
  ) {
    if (apiData) {
      const liqSrcTypes = {};
      apiData.forEach(row => {
        const type = capitalizeString(row[colNameForGrouping]);
        if (this.isValidType(type)) {
          const key = row[colNameForKey];
          const value = row[colNameForValue];
          if (!liqSrcTypes[type]) {
            liqSrcTypes[type] = {};
          }
          liqSrcTypes[type][key] = value;
        } else {
          // Invalid Type
          console.error(`The item with type ${type} is not valid and has been ignored in the tree`);
        }
      });

      const newTree = { ...liqSrcTypes };
      return newTree;
    }
    return false;
  }

  public treeBuildNodes(obj: { [key: string]: any }, level: number): EquationsTreeNode[] {
    return Object.keys(obj)
      .sort()
      .reduce<EquationsTreeNode[]>((accumulator, key) => {
        const value = obj[key];
        const node = new EquationsTreeNode();
        node.name = key;
        // Todo: For bug fix 3205. Test it in liq.sources p.eq. too
        // node.name = value;

        if (value !== null) {
          if (typeof value === 'object') {
            node.children = this.treeBuildNodes(value, level + 1);
          } else {
            node.value = value;
          }
        }

        return accumulator.concat(node);
      }, []);
  }

  /**
   * Checks if the parameter is a liquidity source's valid type (case insensitive)
   */
  public isValidType(typeToCheck = null): boolean {
    return typeToCheck && typeof typeToCheck === `string`;
  }

  public getEquationIdentifiers() {
    const url = `${this.environment.CALCAPI}/api/equation/getEquationIdentifiers`;
    return this.http.get(url).pipe(backoff(3, 250));
  }
}
